/**
 * Trip Alert Tracking Service
 * 
 * Tracks alerts managed during trips and generates post-trip summaries
 */

import { collection, doc, getDoc, setDoc, updateDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { TripAlert } from '@/utils/tripAlerts';

export type AlertTracking = {
  tripId: string;
  alertsManaged: Array<{
    alertId: string;
    type: TripAlert['type'];
    priority: TripAlert['priority'];
    managedAt: Date;
    action?: string;
  }>;
  totalAlerts: number;
  criticalAlerts: number;
  flawlessTrip: boolean; // No critical alerts
  lastUpdated: Date;
};

const COLLECTION_NAME = 'tripAlertTracking';

/**
 * Track an alert being managed/resolved
 */
export async function trackAlertManaged(
  tripId: string,
  alert: TripAlert,
  action?: string
): Promise<void> {
  try {
    const trackingRef = doc(db, COLLECTION_NAME, tripId);
    const trackingDoc = await getDoc(trackingRef);

    const now = new Date();
    const alertEntry = {
      alertId: alert.id,
      type: alert.type,
      priority: alert.priority,
      managedAt: now,
      action,
    };

    if (trackingDoc.exists()) {
      const data = trackingDoc.data();
      const alertsManaged = data.alertsManaged || [];
      
      // Check if this alert was already tracked
      const existingIndex = alertsManaged.findIndex(
        (a: any) => a.alertId === alert.id
      );

      if (existingIndex >= 0) {
        // Update existing entry
        alertsManaged[existingIndex] = alertEntry;
      } else {
        // Add new entry
        alertsManaged.push(alertEntry);
      }

      // Calculate stats
      const criticalAlerts = alertsManaged.filter(
        (a: any) => a.priority === 'high'
      ).length;
      const flawlessTrip = criticalAlerts === 0;

      await updateDoc(trackingRef, {
        alertsManaged,
        totalAlerts: alertsManaged.length,
        criticalAlerts,
        flawlessTrip,
        lastUpdated: now,
      });
    } else {
      // Create new tracking document
      const criticalAlerts = alert.priority === 'high' ? 1 : 0;
      await setDoc(trackingRef, {
        tripId,
        alertsManaged: [alertEntry],
        totalAlerts: 1,
        criticalAlerts,
        flawlessTrip: criticalAlerts === 0,
        lastUpdated: now,
      });
    }
  } catch (error) {
    console.error('[TripAlertTracking] Error tracking alert:', error);
  }
}

/**
 * Get alert tracking for a trip
 */
export async function getTripAlertTracking(
  tripId: string
): Promise<AlertTracking | null> {
  try {
    const trackingRef = doc(db, COLLECTION_NAME, tripId);
    const trackingDoc = await getDoc(trackingRef);

    if (!trackingDoc.exists()) {
      return null;
    }

    const data = trackingDoc.data();
    return {
      tripId: data.tripId,
      alertsManaged: (data.alertsManaged || []).map((a: any) => ({
        ...a,
        managedAt: a.managedAt?.toDate() || new Date(),
      })),
      totalAlerts: data.totalAlerts || 0,
      criticalAlerts: data.criticalAlerts || 0,
      flawlessTrip: data.flawlessTrip ?? false,
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
    };
  } catch (error) {
    console.error('[TripAlertTracking] Error getting tracking:', error);
    return null;
  }
}

/**
 * Generate post-trip alert summary
 */
export function generatePostTripSummary(tracking: AlertTracking | null): {
  title: string;
  message: string;
  badge?: string;
  stats: {
    totalAlerts: number;
    criticalAlerts: number;
    flawlessTrip: boolean;
  };
} {
  if (!tracking || tracking.totalAlerts === 0) {
    return {
      title: 'Flawless Trip!',
      message: 'You had no alerts during this trip. Everything went smoothly!',
      badge: '🌟 Flawless',
      stats: {
        totalAlerts: 0,
        criticalAlerts: 0,
        flawlessTrip: true,
      },
    };
  }

  if (tracking.flawlessTrip) {
    return {
      title: 'Smooth Sailing!',
      message: `You managed ${tracking.totalAlerts} alert${tracking.totalAlerts > 1 ? 's' : ''} during your trip, all handled successfully.`,
      badge: '✅ All Clear',
      stats: {
        totalAlerts: tracking.totalAlerts,
        criticalAlerts: 0,
        flawlessTrip: true,
      },
    };
  }

  return {
    title: 'Trip Completed',
    message: `You managed ${tracking.totalAlerts} alert${tracking.totalAlerts > 1 ? 's' : ''} during your trip, including ${tracking.criticalAlerts} critical ${tracking.criticalAlerts > 1 ? 'ones' : 'one'}.`,
    badge: undefined,
    stats: {
      totalAlerts: tracking.totalAlerts,
      criticalAlerts: tracking.criticalAlerts,
      flawlessTrip: false,
    },
  };
}

